import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { getErrorMessage } from "@/services/api";
import { BrandFullLogo, BrandInline } from "@/components/common/BrandLogo";

const BULLETS = [
    { title: "ERP + CMMS + EAM", desc: "Ativos, técnicos, clientes e ordens em um só lugar." },
    { title: "Ativos com QR Code", desc: "Rastreabilidade completa dos equipamentos de engenharia." },
    { title: "OCR e IA", desc: "Prepare-se para leitura automática de placas e insights preditivos." },
];

export default function LoginPage() {
    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    React.useEffect(() => {
        if (isAuthenticated) {
            const to = location.state?.from?.pathname || "/dashboard";
            navigate(to, { replace: true });
        }
    }, [isAuthenticated, location.state, navigate]);

    async function submit(e) {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Bem-vindo(a) ao Prosper360!");
            const to = location.state?.from?.pathname || "/dashboard";
            navigate(to, { replace: true });
        } catch (err) {
            const msg = getErrorMessage(err, "E-mail ou senha inválidos.");
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="grid min-h-screen w-full grid-cols-1 lg:grid-cols-2">
            {/* Left brand panel */}
            <div className="relative hidden overflow-hidden bg-brand-gradient text-primary-foreground lg:block">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage:
                            "radial-gradient(900px 500px at 15% 20%, rgba(201,162,78,0.14), transparent 60%), radial-gradient(700px 400px at 85% 80%, rgba(75,135,185,0.18), transparent 60%)",
                    }}
                />
                <div className="relative z-10 flex h-full flex-col justify-between p-10">
                    <BrandInline subtitle="Engenharia Integrada" />

                    <div className="grid gap-8 md:grid-cols-[auto,1fr] md:items-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.4 }}
                            className="mx-auto"
                        >
                            <BrandFullLogo size={220} className="ring-1 ring-white/10" />
                        </motion.div>
                        <div className="max-w-md space-y-5">
                            <h1 className="text-3xl font-semibold leading-tight tracking-tight">
                                <span className="text-brand-gold-shine">Opere</span> sua engenharia com clareza, controle e velocidade.
                            </h1>
                            <ul className="space-y-3">
                                {BULLETS.map((b, i) => (
                                    <motion.li
                                        key={b.title}
                                        initial={{ opacity: 0, y: 4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.15 + i * 0.06 }}
                                        className="flex items-start gap-3"
                                    >
                                        <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gold" />
                                        <div>
                                            <div className="text-sm font-medium text-white/95">{b.title}</div>
                                            <div className="text-sm text-white/60">{b.desc}</div>
                                        </div>
                                    </motion.li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="text-xs text-white/50">
                        © {new Date().getFullYear()} Prosper360 Engenharia Integrada · Módulo 01 · Core Platform
                    </div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6 sm:p-10">
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.24 }}
                    className="w-full max-w-md"
                >
                    <div className="mb-6 flex justify-center lg:hidden">
                        <BrandInline subtitle="Engenharia Integrada" />
                    </div>
                    <Card className="rounded-2xl border shadow-sm">
                        <CardContent className="space-y-5 p-6 sm:p-8">
                            <div className="space-y-1">
                                <div className="text-2xl font-semibold tracking-tight">Entrar</div>
                                <p className="text-sm text-muted-foreground">
                                    Use seu e-mail corporativo para acessar o Prosper360.
                                </p>
                            </div>

                            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                                <div className="space-y-1.5">
                                    <Label htmlFor="email">E-mail</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        data-testid="login-email-input"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="password">Senha</Label>
                                        <Link
                                            to="/forgot-password"
                                            className="text-xs font-medium text-secondary hover:underline"
                                            data-testid="login-forgot-password-link"
                                        >
                                            Esqueci minha senha
                                        </Link>
                                    </div>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPass ? "text" : "password"}
                                            autoComplete="current-password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            data-testid="login-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPass((s) => !s)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                                            data-testid="login-show-password"
                                        >
                                            {showPass ? "ocultar" : "mostrar"}
                                        </button>
                                    </div>
                                </div>

                                {error && (
                                    <div
                                        className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger"
                                        data-testid="login-error"
                                    >
                                        {error}
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full bg-brand-navy text-primary-foreground hover:bg-brand-navy/90"
                                    disabled={submitting}
                                    data-testid="login-submit-button"
                                >
                                    {submitting ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <LogIn className="mr-2 h-4 w-4" />
                                    )}
                                    Entrar
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </div>
    );
}
